// Phase 3 — Active team context. Drives every team-scoped page.
//
// Behavior:
//   - On first paint, default to the team with code PITT_GABRUZ_CRICKET if
//     the user belongs to it; otherwise the first team they're in; otherwise
//     (for elevated users with no team membership) the "all teams" sentinel.
//   - Selection persists in localStorage so the user lands back on the same
//     team after refresh / re-login.
//   - Elevated users (ADMIN / PRESIDENT / VICE_PRESIDENT) get an extra
//     "All Teams" option that drops every team filter. Regular players can
//     only switch among teams they're actually on.
//   - If the stored team disappears (left the team, deleted, etc.) the
//     provider falls back to the default automatically.
//
// Views consume this via `useActiveTeam()`; consumers filter their own
// data by checking `isAllTeams` or matching against `activeTeamId`.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ActiveTeamCtx = createContext(null);

const STORAGE_KEY = "gabruz-active-team";
const PINNED_DEFAULT_CODE = "PITT_GABRUZ_CRICKET";
const ELEVATED_ROLES = ["ADMIN", "PRESIDENT", "VICE_PRESIDENT"];

function isElevated(user) {
  return user?.roles?.some(r => ELEVATED_ROLES.includes(r)) || false;
}

function readPref() {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function writePref(val) {
  try {
    if (val === null || val === undefined) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(val));
  } catch { /* ignore */ }
}

function pickDefault(availableTeams, elevated) {
  const pinned = availableTeams.find(t => t.code === PINNED_DEFAULT_CODE);
  if (pinned) return pinned.id;
  // Then any cricket team
  const anyCricket = availableTeams.find(t => t.sport === "Cricket");
  if (anyCricket) return anyCricket.id;
  if (availableTeams.length > 0) return availableTeams[0].id;
  return elevated ? "all" : null;
}

export function ActiveTeamProvider({ user, teams, children }) {
  const elevated = isElevated(user);

  // Teams the user is actually on. Elevated users see all teams in the
  // switcher; regular players see their own.
  //
  // "Their own" = roster member (t.players) OR captain (t.captainId) OR
  // vice / Summer Volleyball sub-team-B captain (t.captainBId). The
  // captain checks are a safety net — a captain / VC who was set on the
  // team record but never added to sports.TeamPlayers would otherwise
  // be invisible to themselves in the switcher.
  const memberTeams = useMemo(
    () => (teams || []).filter(t =>
      (t.players || []).includes(user.id) ||
      t.captainId  === user.id ||
      t.captainBId === user.id
    ),
    [teams, user.id]
  );
  const availableTeams = elevated ? (teams || []) : memberTeams;

  const [activeTeamId, setActiveTeamIdRaw] = useState(() => {
    const pref = readPref();
    if (pref === "all" && elevated) return "all";
    if (pref) {
      const id = Number(pref);
      if (Number.isFinite(id) && availableTeams.some(t => t.id === id)) return id;
    }
    return pickDefault(availableTeams, elevated);
  });

  // If the selected team disappears from `availableTeams` (e.g. team data
  // hasn't loaded yet on first paint, or user left the team), recompute.
  useEffect(() => {
    if (activeTeamId === "all") return;
    if (activeTeamId == null && availableTeams.length > 0) {
      setActiveTeamIdRaw(pickDefault(availableTeams, elevated));
      return;
    }
    if (activeTeamId != null && !availableTeams.some(t => t.id === activeTeamId)) {
      const next = pickDefault(availableTeams, elevated);
      setActiveTeamIdRaw(next);
      writePref(next == null ? null : next);
    }
  }, [availableTeams, activeTeamId, elevated]);

  const setActiveTeam = useCallback((id) => {
    setActiveTeamIdRaw(id);
    writePref(id == null ? null : id);
  }, []);

  const activeTeam = activeTeamId === "all" || activeTeamId == null
    ? null
    : (teams || []).find(t => t.id === activeTeamId) || null;
  const isAllTeams = activeTeamId === "all";

  const value = useMemo(() => ({
    activeTeamId,
    activeTeam,
    availableTeams,
    setActiveTeam,
    isAllTeams,
    elevated,
    // Helper for views: does this row's teamId match the active filter?
    matchesActiveTeam: (teamId) => isAllTeams || teamId === activeTeamId
  }), [activeTeamId, activeTeam, availableTeams, setActiveTeam, isAllTeams, elevated]);

  return (
    <ActiveTeamCtx.Provider value={value}>
      {children}
    </ActiveTeamCtx.Provider>
  );
}

export function useActiveTeam() {
  const ctx = useContext(ActiveTeamCtx);
  if (!ctx) throw new Error("useActiveTeam must be used inside <ActiveTeamProvider>");
  return ctx;
}
